import {Box, Radio} from '@yandex-delivery/blocks'
import React, {useCallback, useMemo, useState} from 'react'
import {useController, useFormContext} from 'react-hook-form'

import {useAction} from '../../actions'
import type {ActionComponentProps} from '../../actions/types'
import {useFormUserEventHandler} from '../../lib/metrika'
import type {OrderFormState} from '../../types'
import type {RadioWidgetProps as Props} from '../types'

export const RadioWidget = ({
  config: {action, enabled, metrica_label, name, value: externalValue},
}: Props): React.ReactElement => {
  const {control} = useFormContext<OrderFormState>()
  const {
    field: {value: fieldValue, onChange, ...field},
  } = useController({
    name,
    control,
  })

  const handleReachGoal = useFormUserEventHandler()

  const [innerValue, setInnerValue] = useState(externalValue)

  const handleActionChange = useCallback(
    (val: string) => {
      onChange(val)
      setInnerValue(val)
    },
    [onChange],
  )

  const actionProps = useMemo<Pick<ActionComponentProps, 'onChange'>>(
    () => ({
      onChange: handleActionChange,
    }),
    [handleActionChange],
  )

  const {ActionComponent, triggerAction} = useAction(action, actionProps)

  const handleClick = useCallback(() => {
    metrica_label && handleReachGoal(metrica_label)

    if (triggerAction) {
      triggerAction()
      return
    }

    onChange(externalValue)
  }, [externalValue, handleReachGoal, metrica_label, onChange, triggerAction])

  return (
    <Box>
      <Radio
        {...field}
        checked={(fieldValue !== void 0 && fieldValue === innerValue) || fieldValue === externalValue}
        disabled={!enabled}
        onClick={handleClick}
        value={innerValue || externalValue}
      />

      {ActionComponent}
    </Box>
  )
}
